import { useEffect, useMemo } from 'react'
import { Alert, Col, Form, Input, Modal, Row, Select, Switch, Tooltip, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  ACCOUNT_TYPES,
  BALANCE_SHEET_TYPES,
  CASH_FLOW_CLASSES,
  collectAccountSubtree,
  createAccount,
  updateAccount,
  type Account,
  type AccountDraft,
  type AccountType,
  type CashFlowClass,
} from './financeApi'

interface AccountFormModalProps {
  open: boolean
  /** `null` — yangi hisob */
  account: Account | null
  /**
   * BUTUN reja — ota tanlash va sikl tekshiruvi uchun.
   *
   * ⚠️ Bu yerga sahifaning KO'RINADIGAN ro'yxatini bermang. 2026-08-20
   * dan rejada sukut bo'yicha faqat ochiq hisoblar ko'rinadi; o'sha
   * qisqargan ro'yxat kelsa mavjud hisobning YOPILGAN otasi tanlovda
   * topilmasdi va maydon «otasi yo'q» bo'lib ko'rinardi — saqlash uni
   * jimgina ildizga ko'chirardi. Sikl qorovuli
   * ({@link collectAccountSubtree}) ham to'liq to'plamsiz halqani
   * ko'rmaydi.
   */
  accounts: readonly Account[]
  /**
   * YANGI hisob uchun oldindan tanlangan ota (`account === null` da).
   *
   * ⚠️ Tahrirlashda E'TIBORGA OLINMAYDI: u yerda ota mavjud hisobning
   * o'zinikidan olinadi va bu qiymat uni JIMGINA boshqa shoxga
   * ko'chirardi — hisoblar rejasida bu butun bo'limni yo'qotish demak.
   */
  defaultParentId?: string | null
  onClose: () => void
}

interface FormValues {
  parentId?: string | null
  code: string
  name: string
  type: AccountType
  postable: boolean
  requiresDimension: boolean
  active: boolean
  cashFlow?: CashFlowClass | null
}

const CODE_PATTERN = /^[A-Za-z0-9._-]{1,32}$/
const NAME_MAX = 200

/**
 * Hisob yaratish va tahrirlash.
 *
 * <h2>⚠️ HISOBLAR REJASI SOZLANADIGAN, KODDA SANAB QO'YILMAGAN</h2>
 * `docs/10-MIKROSERVIS.md` §13.6. Migratsiyadagi boshlang'ich ro'yxat —
 * MA'LUMOT, qaror emas: operator o'z rejasini quradi. Shuning uchun bu
 * oynada hisob nomlari ham, kodlari ham qattiq yozilmagan.
 *
 * <h2>⚠️ TUR ESA YOPIQ — VA BU ZIDDIYAT EMAS</h2>
 * `AccountDraft` javadoc'i sababini yozadi: hisob turi buxgalteriyaning
 * asosiy tenglamasi (aktiv = passiv + kapital) va u tashkilotdan
 * tashkilotga o'zgarmaydi. `platform.org_node_type` da dinamiklik KERAK
 * edi, bu yerda esa u "foyda hisobotini qurib bo'lmaydi" degani bo'lardi.
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q — SERVERDA HAM YO'Q</h2>
 * Hisobga provodka yozilgan bo'lishi mumkin, provodka esa O'ZGARMAS
 * (`finance.journal_immutable()`). O'chirish jurnalni "hisobi noma'lum
 * satrlar" bilan qoldirardi. Ishlatilmaydigan hisob `active = false`
 * bilan YOPILADI va yangi provodkada tanlanmaydi.
 *
 * <h2>⚠️ SERVER QOIDALARI SHU YERDA TAKRORLANADI — YASHIRISH UCHUN EMAS</h2>
 * Uchta qoida oldindan qo'llanadi (guruh hisobda o'lchov talab
 * qilinmaydi; tizim hisobi o'chirilmaydi; tizim hisobi guruhga
 * aylanmaydi). Ular baribir serverda ham, bazada ham tekshiriladi —
 * bu yerdagisi HIMOYA emas, TUSHUNTIRISH: operator sababini tugmani
 * bosishdan OLDIN o'qiydi, 400 xatosidan keyin taxmin qilmaydi.
 */
export function AccountFormModal({
  open,
  account,
  accounts,
  defaultParentId = null,
  onClose,
}: AccountFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const editing = account !== null

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      parentId: account === null ? defaultParentId : account.parentId,
      code: account?.code ?? '',
      name: account?.name ?? '',
      type: (account?.type as AccountType | undefined) ?? 'EXPENSE',
      // Yangi hisob standart holda PROVODKA YOZILADIGAN: guruh hisob —
      // istisno, kundalik hol emas.
      postable: account?.postable ?? true,
      requiresDimension: account?.requiresDimension ?? false,
      active: account?.active ?? true,
      cashFlow: (account?.cashFlow as CashFlowClass | null | undefined) ?? null,
    })
  }, [open, account, defaultParentId, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft: AccountDraft = {
        parentId: values.parentId ?? null,
        code: values.code.trim(),
        name: values.name.trim(),
        type: values.type,
        postable: values.postable,
        // ⚠️ Guruh hisobda MAJBURAN `false`: maydon o'chirilgan bo'lsa
        // ham formada eski `true` qolib ketishi mumkin va server 400
        // berardi ("guruh hisobda xarajat markazi talab qilinmaydi").
        requiresDimension: values.postable && values.requiresDimension,
        active: values.active,
        // ⚠️ SINF SAQLANADI, formadan kelmaydi. `PUT` — TO'LIQ
        // ALMASHTIRISH: maydon yuborilmasa server uni saqlab qolmaydi,
        // `EXPENSE` uchun `OPERATING` ga TUSHIRADI
        // (`AccountDraft.DEFAULT_EXPENSE_CLASS`). Jonli o'lchandi
        // (Staging, 2026-08-16): `5010`/`5300` kabi `COGS` hisobning
        // faqat NOMI tahrirlanganda ham sinfi `OPERATING` bo'lib
        // qolardi — yalpi foyda daromadga tenglashib, marja 100%
        // ko'rinardi (`V202608151500` 5-bo'limi aynan shundan
        // ogohlantiradi).
        //
        // ⚠️ Tur `EXPENSE` dan chiqsa MAJBURAN `null`: aks holda server
        // 400 berardi ("sinf faqat EXPENSE turidagi hisobda ma'noli").
        // Yangi hisobda `null` — sinfni server o'z zaxira qiymati bilan
        // to'ldiradi.
        expenseClass: values.type === 'EXPENSE' ? (account?.expenseClass ?? null) : null,
        cashFlow: BALANCE_SHEET_TYPES.includes(values.type) ? (values.cashFlow ?? null) : null,
      }
      return account === null ? createAccount(draft) : updateAccount(account.id, draft)
    },
    onSuccess: () => {
      // ⚠️ `finance.all` PREFIKS: yangi hisob xarajat oynasining
      // ro'yxatida ham, rejada ham ko'rinishi kerak — ikkalasi ikki
      // kalitda (`activeOnly` kalitning bir qismi).
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — eski xato xabari keyingi ochilishda chiqmasin */
  const close = () => {
    save.reset()
    onClose()
  }

  /**
   * Ota bo'la oladigan hisoblar.
   *
   * ⚠️ O'ZI va BUTUN AVLODI chiqarib tashlanadi. Bazada faqat
   * `parent_id <> id` cheklovi bor, uzun halqa (A → B → A) esa undan
   * o'tib ketardi — va oqibati JIM: halqadagi shox daraxtda ildizga
   * chiqmaydi, ya'ni hisoblar rejasining bir qismi ekrandan yo'qoladi.
   */
  const parentOptions = useMemo(() => {
    const forbidden =
      account === null ? new Set<string>() : collectAccountSubtree(accounts, account.id)
    return accounts
      .filter((item) => !forbidden.has(item.id))
      .map((item) => ({
        value: item.id,
        label: `${item.code} · ${seededName('accounts', item.code, item.name, t)}`,
      }))
  }, [accounts, account, t])

  const postable = Form.useWatch('postable', form)
  const type = Form.useWatch('type', form)

  /**
   * ⚠️ Tizim hisobi — `finance.ledger_map` unga TAYANADI. Uni yopish
   * yoki guruhga aylantirish davr yopishni sindirardi va sabab butunlay
   * boshqa joyda izlanardi ("oy oxirida davr yopilmayapti").
   */
  const systemLocked = account?.system === true

  return (
    <Modal
      open={open}
      title={editing ? t('finance.account.editTitle') : t('finance.account.createTitle')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
      width={640}
    >
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {/* 409 ("bunday kod bor") va 400 SHU YERDA qoladi — oyna ochiq,
            ya'ni operator xabarni tuzatishi kerak bo'lgan maydon bilan
            bir ekranda ko'radi (`VoucherVoidModal` naqshi) */}
        {save.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(save.error, t)}
          />
        ) : null}

        {systemLocked ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('finance.account.systemTitle')}
            description={t('finance.account.systemBody')}
          />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="code"
              label={t('finance.account.code')}
              extra={t('finance.account.codeHint')}
              rules={[
                { required: true, whitespace: true, message: t('finance.account.codeRequired') },
                { pattern: CODE_PATTERN, message: t('finance.account.codeShape') },
              ]}
            >
              <Input maxLength={32} autoFocus={!editing} />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              name="name"
              label={t('finance.account.name')}
              rules={[
                { required: true, whitespace: true, message: t('finance.account.nameRequired') },
                { max: NAME_MAX, message: t('finance.account.nameTooLong') },
              ]}
            >
              <Input maxLength={NAME_MAX} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="type"
              label={t('finance.account.type')}
              extra={t('finance.account.typeHint')}
              rules={[{ required: true, message: t('finance.account.typeRequired') }]}
            >
              <Select
                options={ACCOUNT_TYPES.map((type) => ({
                  value: type,
                  label: t(`accountType.${type}`),
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="parentId"
              label={t('finance.account.parent')}
              extra={t('finance.account.parentHint')}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={parentOptions}
                placeholder={t('finance.account.parentNone')}
                notFoundContent={t('finance.account.parentEmpty')}
              />
            </Form.Item>
          </Col>
        </Row>

        {type && BALANCE_SHEET_TYPES.includes(type) ? (
          <Form.Item
            name="cashFlow"
            label={t('finance.account.cashFlow')}
            extra={t('finance.account.cashFlowHint')}
          >
            <Select
              allowClear
              placeholder={t('finance.account.cashFlowAuto')}
              options={CASH_FLOW_CLASSES.map((value) => ({
                value,
                label: t(`cashFlowClass.${value}`),
              }))}
            />
          </Form.Item>
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="postable"
              label={t('finance.account.postable')}
              extra={t('finance.account.postableHint')}
              valuePropName="checked"
            >
              <Switch disabled={systemLocked} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="requiresDimension"
              label={t('finance.account.requiresDimension')}
              extra={
                postable === false
                  ? t('finance.account.requiresDimensionBlocked')
                  : t('finance.account.requiresDimensionHint')
              }
              valuePropName="checked"
            >
              {/* ⚠️ Guruh hisobga provodka yozilmaydi, ya'ni unda o'lchov
                  talab qilish MA'NOSIZ va operatorni chalg'itardi
                  ("o'lchov majburiy dedim, lekin yozib bo'lmayapti") */}
              <Switch disabled={postable === false} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="active"
              label={t('finance.account.active')}
              extra={t('finance.account.activeHint')}
              valuePropName="checked"
            >
              {systemLocked ? (
                <Tooltip title={t('finance.account.systemBody')}>
                  {/* `disabled` element tooltip'ni yutadi — o'rov kerak */}
                  <span>
                    <Switch disabled />
                  </span>
                </Tooltip>
              ) : (
                <Switch />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Typography.Text type="secondary">{t('finance.account.formNote')}</Typography.Text>
      </Form>
    </Modal>
  )
}
